import { gql } from 'graphql-request';
import {
	COMPETITORS_PRICE_RATE_ALERT_EXECUTION_NOTIFICATION,
	COMPETITORS_PRICE_RATE_ALERT_RULE_FRAGMENT,
	COMPETITORS_SOLD_OUT_ALERT_EXECUTION_NOTIFICATION,
	COMPETITORS_SOLD_OUT_ALERT_RULE_FRAGMENT,
	PARITY_PRICE_COMPARISON_ALERT_EXECUTION_NOTIFICATION,
	PARITY_PRICE_COMPARISON_ALERT_RULE_FRAGMENT,
	PRICE_COMPARISON_ALERT_EXECUTION_NOTIFICATION,
	PRICE_COMPARISON_ALERT_RULE_FRAGMENT,
} from '@app/model/alert/service/AlertGraphQLFragments';

export const MARKET_STATE_DAILY_SNAPSHOT_FRAGMENT = gql`
    fragment MarketStateDailySnapshotFields on MarketStateStatDateSnapshot {
        marketStateIds
        requestIds
        statDate
        latestUpdateDate
        weightedAveragePrice { amount currency }
        roomWeightedAverages { roomMappingType price { amount currency } }
        competitorsPriceStatistics {
            medianPrice {
                currency
                amount
            }
            minPrice {
                currency
                amount
            }
            maxPrice {
                currency
                amount
            }
        }
        competitorSnapshots {
            competitorId
            isGroup
            isMainHotel

            pmsAvailability {
                creationDate
                availability
            }
            lowestItemIndex
            lowestItemIndicesPerRoomMappingType {
                roomMappingType
                index
            }
            availabilityEvolutionItems {
                hotelId
                roomMappingType
                availability
                representativeRoomId
                roomItems {
                    roomId
                    roomName
                    availability
                    price {
                        currency
                        amount
                    }
                }
            }
            lowestItemPerHotelIdAndRoomIds {
                hotelId
                roomId
                roomMappingType
                availability
                availabilityIndex
                isNotSupported
                lowestItem {
                    providerPlatforms {
                        provider
                        platform
                    }
                    region
                    isEmpty
                    isAvailable
                    isNotAvailable
                    isSoldOut
                    isNotSupported
                    isLos
                    item {
                        id
                        hasProof
                        proofChoiceId
                        baseRate
                        cancellationDeadline
                        promotions {
                            combinedPercentage
                            components {
                                code
                                percentage
                                amount
                                sourceType
                                sourceSubtype
                            }
                        }
                        preferredTier
                        creationDate
                        isPartnerOffer
                        price {
                            currency
                            amount
                        }
                        state
                        isNotSupported
                        leastPeriodOfStay
                        hasMemberDiscount
                        hasMobileDiscount
                        meal
                        refunding
                    }
                }
            }
        }
    }
`;


export const MARKET_STATE_FOR_DAY_NOTIFICATIONS = gql`
    ${PRICE_COMPARISON_ALERT_EXECUTION_NOTIFICATION}
    ${COMPETITORS_PRICE_RATE_ALERT_EXECUTION_NOTIFICATION}
    ${COMPETITORS_PRICE_RATE_ALERT_RULE_FRAGMENT}
    ${PRICE_COMPARISON_ALERT_RULE_FRAGMENT}
    ${PARITY_PRICE_COMPARISON_ALERT_EXECUTION_NOTIFICATION}
    ${PARITY_PRICE_COMPARISON_ALERT_RULE_FRAGMENT}
    ${COMPETITORS_SOLD_OUT_ALERT_EXECUTION_NOTIFICATION}
    ${COMPETITORS_SOLD_OUT_ALERT_RULE_FRAGMENT}
    fragment MarketStateForDayNotificationsFragment on MarketStateForDayNotificationsOutput {
        companyHotelId
        statDate
        hasNotifications
        priceComparisonAlertExecutionNotification {
            ...PriceComparisonAlertExecutionNotificationFields
            alert {
                id
                type
                name
            }
            alertRule {
                ...PriceComparisonAlertRuleFields
            }
        }
        competitorsPriceRateAlertExecutionNotification {
            ...CompetitorsPriceRateAlertExecutionNotificationFields
            alert {
                id
                type
                name
            }
            alertRule {
                ...CompetitorsPriceRateAlertRuleFields
            }
        }
        parityPriceComparisonAlertExecutionNotification {
            ...ParityPriceComparisonAlertExecutionNotificationFields
            alert {
                id
                type
                name
            }
            alertRule {
                ...ParityPriceComparisonAlertRuleFields
            }
        }
        competitorsSoldOutAlertExecutionNotification {
            ...CompetitorsSoldOutAlertExecutionNotificationFields
            alert {
                id
                type
                name
            }
            alertRule {
                ...CompetitorsSoldOutAlertRuleFields
            }
        }
    }
`;

export const COMPANY_HOTEL_MARKET_STATE_ITEM_FRAGMENT = gql`
    fragment MarketStateItemFragment on MarketStateItem {
        id
        hasProof
        marketStateId
        hotelId
        creationDate
        roomId
        roomType
        isPartnerOffer
        promotions {
            combinedPercentage
            components {
                code
                percentage
                amount
                sourceType
                sourceSubtype
            }
        }
        preferredTier
        price {
            amount
            currency
        }
        state
        leastPeriodOfStay
				filter {
						roomMappingType
						guests
						meal
						refunding
						hasMemberDiscount
						hasMobileDiscount
				}
    }
`;

export const PRICE_RECOMMENDATION_STATE_FRAGMENT = gql`
    fragment PriceRecommendationStateFragment on PriceRecommendationState {
        id
        requestId
        companyHotelId
        architectureId
        statDate
        roomMappingType
        creationDate
        recommendedChangeRate
        effectiveChangeRate
        confidence
        narrative
        priceRuleLimited
        driversExplanation
        direction
        isValid
        drivers {
            type
            impact
        }
				minimumBasePriceRoomStates {
            roomId
            roomName
            isAnchor
            isDerivative
            adults
            children
            basePrice
            changeRate
            finalPrice
        }
        marketStatePromptId
    }`;

export const MARKET_STATE_DAILY_SNAPSHOT_WITH_COMPARISON_FRAGMENT = gql`
    ${MARKET_STATE_DAILY_SNAPSHOT_FRAGMENT}
    ${PRICE_RECOMMENDATION_STATE_FRAGMENT}
    fragment MarketStateDailySnapshotWithComparisonOutputFragment on MarketStateDailySnapshotWithComparisonOutput {
        recommendationMarketUpdateDate
        current {
            ...MarketStateDailySnapshotFields
        }
        previous {
            ...MarketStateDailySnapshotFields
        }
        statDate
				priceRecommendationStateSnapshot {
            recommendation {
                ...PriceRecommendationStateFragment
            }
            isSubmitted
						hasRecommendation
						recommendedPrice
            recommendationPosition
            effectiveChangeRate
        }
        allPriceRecommendationStates {
            ...PriceRecommendationStateFragment
				}
				submittedRecommendationStateIds
        
    }`;
